// OBW Journey Matrix (ADR-021).
// Five MVP journeys — each row defines allowed steps, required steps,
// autoFill source, exit policy, allowed actors, exit writes, and login-check
// effect. The <OBWWizard> component reads this table at runtime.

export type JourneyId =
  | 'sign-up'
  | 'manual-onboard-no-plan'
  | 'manual-onboard-with-plan'
  | 'add-second-domain'
  | 'manage-account'
  // PL3 (UAT iter-2) — "Choose Plan" on an EXISTING no-plan domain. The SHORT
  // tailored journey: subscribe → payment (skipped when a card is already on
  // file, via OBWWizard skipSteps) → activation. It deliberately omits the
  // sign-up-only steps (welcome/domain-verify/profile/persona/brand) — the
  // account is already onboarded; this only puts a plan on one existing domain.
  | 'subscribe-existing-domain'
  // Phase 5c.5 (ADR-030 D3) — POW restructured as OBW journey.
  | 'partner-apply'
  // ADR-073 Amendment 2 (A2.2) — BOW (Beta Onboarding Workflow): admin-assisted
  // beta participant onboarding. Account-less until bow-complete provisions.
  | 'bow'
  // ADR-078 §4 — B2S (beta → subscriber): the "tail of SOW". Staff-mounted via an
  // admin-trusted launch; converts an EXISTING beta account to paying at
  // b2s-complete. Reuses the subscribe + payment(stub) renderers.
  | 'b2s';

export type ObwStepId =
  | 'welcome'
  // ADR-124 I3 — "Data residency": the business's country, which decides its zone.
  | 'residency'
  | 'domain-verify'
  | 'subscribe'
  | 'payment'
  | 'profile'
  | 'persona'
  | 'brand'
  // (The snippet is no longer a wizard step — removed from every journey per
  // ADR-073 A2.3 and delivered by email post-activation per ADR-076 §5;
  // renderers deleted.)
  | 'activation'
  // Phase 5c.5 (ADR-030 D3) — partner-apply journey step IDs.
  | 'pa-welcome'
  | 'pa-terms'
  | 'pa-prefill'
  | 'pa-business'
  | 'pa-submit'
  | 'pa-status'
  // ADR-073 Amendment 2 (A2.2) — BOW bookend modals.
  | 'bow-welcome'
  | 'bow-complete'
  // ADR-078 §4 — B2S conversion bookend.
  | 'b2s-complete';

export type AutoFill = 'website-ai' | 'signup-form-only' | 'existing-subscriber-data' | 'none';
export type ExitPolicy = 'rollback' | 'save';
export type AllowedActor = 'subscriber' | 'nexa-admin';

export interface JourneyConfig {
  journeyId: JourneyId;
  steps: ObwStepId[];
  required: ObwStepId[];
  autoFill: AutoFill;
  exitPolicy: ExitPolicy;
  allowedActors: AllowedActor[];
  /**
   * PL32 — when true, the wizard MINTS the Domain at the `domain-verify` step:
   * it calls the uniqueness-guarded `POST /portal/accounts/{id}/domains` write,
   * captures the returned `domainToken` into engine state, and only then
   * advances. Set for the add-second-domain journey, which starts with no token
   * (unlike subscribe-existing-domain, whose mount passes an existing token).
   * Data-driven, not journeyId-sniffed (ADR-077 L5): the flag is the signal.
   * Journeys with their own provisioning path (sign-up rollback, BOW/manual
   * account-less flows) leave it false so the wizard does not double-create.
   */
  createsDomain?: boolean;
  loginCheckEffect:
    | 'none'
    | 'block-dashboard-force-payment'
    | 'block-dashboard-force-plan-and-payment'
    | 'nonblocking-banner';
}

export const JOURNEY_MATRIX: Record<JourneyId, JourneyConfig> = {
  'sign-up': {
    journeyId: 'sign-up',
    steps: [
      'welcome',
      // ADR-124 I3 — the residency step, immediately before `domain-verify`: it must precede the
      // first write that mints anything.
      // On this account-bearing journey the answer was given at the public form and is stored;
      // the step DISPLAYS it (read-only, D1) and its validator passes by construction.
      'residency',
      'domain-verify',
      'subscribe',
      'payment',
      // ⭐ ADR-073 Amendment 6 (Jeff, 9 October 2026: "a tighter sign up workflow (excludes
      // business type, personna etc)") — profile, persona and brand LEFT the sign-up. They are the
      // Launchpad's Business profile and Dashboard look steps now (ADR-127), met at the first
      // separate sign-in, never in the payment session. Sign-up ends on the success screen and the
      // snippet email (ADR-076 Am.2).
      // ADR-073 Amendment 2 (A2.3) — snippet removed from EVERY journey's
      // synchronous modal flow. The snippet lifecycle is unchanged, only moved
      // out of the wizard: account validation → snippet email → install →
      // ingest sees first beacon → Domain.setupState → complete.
      'activation',
    ],
    required: ['residency', 'domain-verify', 'subscribe', 'payment'],
    autoFill: 'website-ai',
    // ⭐⭐ ADR-073 Am.5 / ADR-105 Am.4 (5 October 2026) — `save`, and NO `createsDomain`. The public
    // sign-up is card-first again, as decided on 23 June 2026: the account, the login AND the Domain
    // row are all created at PAYMENT, in one step (account `signup-pay.ts`), before this journey
    // ever runs. So there is nothing to mint at `domain-verify` (its validator, `domainCount >= 1`,
    // is satisfied by the Domain payment created) and nothing to release on exit — a customer
    // arriving here has PAID for that domain. The `createsDomain` + `rollback` PAIR (#303/#313)
    // existed only because the account used to exist before payment; it is retired together, as a
    // pair. ⚠ Keeping `rollback` would now be actively harmful: its server handler deletes the
    // account's `pending` domains and stamps it `rolled_back` — on a paying customer.
    //
    // What this journey is now (ADR-073 Am.6): payment, then `activation` — the success screen,
    // whose terminal step runs the sign-up finaliser (ADR-107 Am.2) and emails the snippet; the hub
    // offers it if the modal was closed first.
    exitPolicy: 'save',
    allowedActors: ['subscriber'],
    loginCheckEffect: 'none',
  },
  'manual-onboard-no-plan': {
    journeyId: 'manual-onboard-no-plan',
    steps: ['welcome', 'domain-verify', 'profile', 'persona', 'brand'],
    required: [],
    autoFill: 'signup-form-only',
    exitPolicy: 'save',
    allowedActors: ['nexa-admin'],
    loginCheckEffect: 'block-dashboard-force-plan-and-payment',
  },
  'manual-onboard-with-plan': {
    journeyId: 'manual-onboard-with-plan',
    steps: ['welcome', 'domain-verify', 'subscribe', 'profile', 'persona', 'brand'],
    required: ['subscribe'],
    autoFill: 'signup-form-only',
    exitPolicy: 'save',
    allowedActors: ['nexa-admin'],
    loginCheckEffect: 'block-dashboard-force-payment',
  },
  'add-second-domain': {
    journeyId: 'add-second-domain',
    steps: [
      'welcome',
      'domain-verify',
      'profile',
      'persona',
      'brand',
      // The snippet is revealed at activation (revealSnippet) — it needs the
      // domainToken this journey mints at domain-verify (PL32, createsDomain).
      'activation',
    ],
    required: ['domain-verify'],
    autoFill: 'website-ai',
    exitPolicy: 'save',
    allowedActors: ['subscriber'],
    // PL32 — this journey creates a brand-new Domain; the wizard mints it (and
    // its token) at domain-verify via the uniqueness-guarded add-domain write.
    createsDomain: true,
    loginCheckEffect: 'nonblocking-banner',
  },
  'manage-account': {
    journeyId: 'manage-account',
    steps: [
      'welcome',
      // ADR-124 D1 — shown READ-ONLY from the stored record: the home is immutable once created,
      // so there is no field here and nothing to block Continue. Not required.
      'residency',
      'domain-verify',
      'subscribe',
      'payment',
      'profile',
      'persona',
      'brand',
      // ADR-073 Amendment 2 (A2.3) — snippet removed from the synchronous flow.
      'activation',
    ],
    required: [],
    autoFill: 'existing-subscriber-data',
    exitPolicy: 'save',
    allowedActors: ['subscriber'],
    loginCheckEffect: 'none',
  },
  // PL3 (UAT iter-2) — the SHORT "add a plan to an existing no-plan domain"
  // journey. Steps: subscribe → payment → activation. `payment` is elided at
  // mount time when a card is already on file (OBWWizard skipSteps prop) — the
  // brief's "payment ONLY if no card on file". exitPolicy 'save' (the account
  // exists; nothing to roll back). Subscriber-only.
  'subscribe-existing-domain': {
    journeyId: 'subscribe-existing-domain',
    steps: ['subscribe', 'payment', 'activation'],
    required: ['subscribe'],
    autoFill: 'existing-subscriber-data',
    exitPolicy: 'save',
    allowedActors: ['subscriber'],
    loginCheckEffect: 'none',
  },
  // Phase 5c.5 (ADR-030 D3) — POW partner application as a 6-step OBW journey.
  // Pre-fill is fetched by PartnerPrefillConfirmStep on mount via
  // GET /portal/pow/prefill-token — not driven by journey-level autoFill.
  // Post-completion routing to /dash/:accountId/partner-application is
  // handled inside PartnerStatusStep (no postCompletionRoute field on
  // JourneyConfig).
  'partner-apply': {
    journeyId: 'partner-apply',
    steps: ['pa-welcome', 'pa-terms', 'pa-prefill', 'pa-business', 'pa-submit', 'pa-status'],
    required: ['pa-terms', 'pa-prefill', 'pa-business', 'pa-submit'],
    autoFill: 'none',
    exitPolicy: 'save',
    allowedActors: ['subscriber'],
    loginCheckEffect: 'none',
  },
  // ADR-073 Amendment 2 (A2.2) — BOW (Beta Onboarding Workflow). Admin-run,
  // account-less: data is collected through the journey and the User + Account +
  // Domain are provisioned at bow-complete (no card, no subscribe/payment).
  // autoFill='website-ai' is configured for parity with SOW; the analysis only
  // fires once an account exists, so for BOW it is inert until a follow-up adds
  // account-less analysis (manual entry works today).
  //
  // ⭐ `subscribe` ADDED 29 July 2026 (Jeff's decision, punchlist B13/B14 build). A beta
  // participant's PLAN is now chosen DURING the BOW, by the operator running it, and persisted
  // on the beta account — so the b2s conversion applies a plan that was actually selected
  // rather than one collected at upgrade time by someone who was not in the room.
  //
  // ⚠ PLAN ONLY, NO `payment` STEP — and the absence is deliberate. A beta participant is not
  // charged; adding payment here would collect card details for a subscription that does not
  // exist yet. The plan is recorded as an INTENT (`betaSelectedProduct`/`betaSelectedTerm` on
  // the Account) and only becomes a billing event at conversion.
  'bow': {
    journeyId: 'bow',
    // ADR-124 I3 — `residency` before `domain-verify`: bow-complete mints from this answer.
    steps: ['bow-welcome', 'residency', 'domain-verify', 'subscribe', 'profile', 'persona', 'bow-complete'],
    required: ['residency', 'domain-verify', 'subscribe', 'profile', 'persona'],
    autoFill: 'website-ai',
    exitPolicy: 'save',
    allowedActors: ['nexa-admin'],
    loginCheckEffect: 'none',
  },
  // ADR-078 §4 — B2S (Upgrade). The beta account already has account + domain +
  // snippet + profile + persona, so B2S collects only the DELTA the beta never
  // captured: tier → payment(stub) → conversion. Staff-mounted via an
  // admin-trusted launch (loginCheckEffect 'none'; assess-at-entry routes beta
  // accounts to the dashboard, so this is never auto-resolved). Payment is the
  // existing stub (ADR-073 Am.1) — no Stripe.
  //
  // ⚠ `allowedActors` STAYS `['nexa-admin']` EVEN THOUGH A PARTNER OPERATOR NOW RUNS THIS
  // JOURNEY (T3.2, 29 July 2026) — and that is the correct outcome, not an oversight.
  //
  // `allowedActors` is the ENGINE's privilege TIER (assess.ts `assertTrust`), not an identity
  // claim, and it grants nothing: `OperatorB2sModal` mounts with `actor="nexa-admin"` exactly as
  // the shipped `OperatorBowModal` does for the equally partner-run `bow` journey. Real trust is
  // the HMAC launch token, re-verified server-side at `/portal/b2s/convert`, and
  // `/portal/b2s/launch` independently requires MINT_ROLES **and** an active consent-grant
  // before minting it.
  //
  // Adding `'subscriber'` here would be strictly WEAKER: `assertTrust` admits any authenticated
  // subscriber to any journey that lists them, so it would let a signed-in customer open the
  // conversion wizard client-side — for no gain, since the partner path already works.
  //
  // `subscribe` is still REQUIRED here as the FALLBACK for betas provisioned before the BOW
  // gained its own plan step: those accounts carry no `betaSelectedProduct`, so conversion asks
  // at upgrade time. For a plan-bearing beta the step is answered again and the STORED plan
  // still wins server-side (b2s.ts) — the account's own record beats a re-selection made by
  // whoever happens to be running the upgrade.
  'b2s': {
    journeyId: 'b2s',
    steps: ['subscribe', 'payment', 'b2s-complete'],
    required: ['subscribe', 'payment'],
    autoFill: 'none',
    exitPolicy: 'save',
    allowedActors: ['nexa-admin'],
    loginCheckEffect: 'none',
  },
};

export const MASTER_STEP_LABELS: Record<ObwStepId, string> = {
  welcome: 'Welcome',
  residency: 'Data residency',
  'domain-verify': 'Domain Authorization',
  subscribe: 'Subscribe',
  payment: 'Payment',
  profile: 'Business Profile',
  persona: 'Persona',
  brand: 'Brand',
  activation: 'Activation',
  // Phase 5c.5 (ADR-030 D3) — partner-apply step labels.
  'pa-welcome': 'Welcome',
  'pa-terms': 'Terms',
  'pa-prefill': 'Confirm Details',
  'pa-business': 'Business',
  'pa-submit': 'Submit',
  'pa-status': 'Status',
  // ADR-073 Amendment 2 (A2.2) — BOW bookend labels.
  'bow-welcome': 'Welcome',
  'bow-complete': 'Complete',
  // ADR-078 §4 — B2S conversion bookend.
  'b2s-complete': 'Complete',
};
